import { motion, useReducedMotion } from "framer-motion";
import type * as React from "react";
import { useId } from "react";

import { cn } from "../../lib/cn";

/**
 * SegmentedControl — the ONE pencil "view switcher" primitive (Jul 2026 SOTA).
 *
 * Replaces the old loose-pill look (each pill floating on cream) with a proper
 * segmented control: a slightly-recessed paper TRACK with a hairline, and a
 * single near-white THUMB that GLIDES between segments (Framer Motion shared
 * ``layoutId``). This is the 2026 quiet-UI pattern (Linear / Attio / Stripe):
 * an in-place filter reads as an "adult" control, not a row of chips.
 *
 * Ando pencil mapping:
 *   - Track: warm recessed fill + inset hairline (the "financial" container).
 *   - Thumb: paper-white raised by a WARM shadow-as-border — no blur, no glow.
 *   - Active label: ink-1 semibold; idle: ink-2 (typography-first).
 *   - The single orange cue is the ACTIVE COUNT (never a filled pill / block).
 *   - Counts are inline, ``tabular-nums``, muted, and HIDDEN when zero.
 *   - Motion is gated behind ``prefers-reduced-motion``.
 *
 * Shared by {@link SegmentedTabs} (local state), the workspace ``SubTabBar``
 * (store-backed) and ``SmartSourceChips`` (documents) so every command-bar
 * ``views`` zone looks identical.
 */
export interface SegmentedItem<T extends string> {
  readonly value: T;
  readonly label: React.ReactNode;
  /** Optional count after the label — orange when active, hidden when 0.
   *  A string (e.g. ``"138+"``) is rendered verbatim for "at least N" cases. */
  readonly badge?: number | string;
  /** Optional leading glyph (already sized, e.g. a 12px Lucide icon). */
  readonly icon?: React.ReactNode;
  /** Optional trailing node (e.g. a sync-status dot). */
  readonly trailing?: React.ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  readonly options: readonly SegmentedItem<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** Accessible name for the tablist. */
  readonly ariaLabel: string;
  readonly className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: SegmentedControlProps<T>) {
  const reduceMotion = useReducedMotion();
  // Unique per instance so multiple controls on one page don't share (and
  // animate into) the same sliding thumb.
  const thumbId = `seg-thumb-${useId()}`;
  const thumbTransition = reduceMotion
    ? { duration: 0 }
    : ({ type: "tween", duration: 0.25, ease: "easeOut" } as const);

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        // Recessed paper track + inset warm hairline (concentric with the thumb).
        "inline-flex w-max shrink-0 items-center gap-0.5 rounded-[12px] p-[3px]",
        "bg-[rgba(60,40,20,0.04)] shadow-[inset_0_0_0_1px_rgba(60,40,20,0.06)]",
        className,
      )}
    >
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(opt.value)}
            className={cn(
              "relative inline-flex h-7 shrink-0 items-center rounded-[9px] px-3",
              "text-[12px] leading-none transition-colors duration-150 ease-smooth",
              "focus-visible:outline-none",
              isActive ? "font-semibold text-ink-1" : "font-medium text-ink-2 hover:text-ink-1",
            )}
          >
            {isActive ? (
              <motion.span
                layoutId={thumbId}
                aria-hidden
                transition={thumbTransition}
                className={cn(
                  "absolute inset-0 rounded-[9px] bg-white",
                  // Warm shadow-as-border = the 2026 depth (no blur, no glow).
                  "shadow-[0_0_0_1px_rgba(60,40,20,0.07),0_1px_2px_rgba(60,40,20,0.06)]",
                )}
              />
            ) : null}
            <span className="relative z-10 inline-flex items-center gap-1.5">
              {opt.icon ? (
                <span className="inline-flex shrink-0 items-center" aria-hidden>
                  {opt.icon}
                </span>
              ) : null}
              <span>{opt.label}</span>
              {opt.trailing ? (
                <span className="inline-flex shrink-0 items-center">{opt.trailing}</span>
              ) : null}
              {(typeof opt.badge === "number" ? opt.badge > 0 : Boolean(opt.badge)) ? (
                <span
                  className={cn(
                    "text-[11px] tabular-nums",
                    isActive ? "text-ando-orange" : "text-ink-3/80",
                  )}
                  aria-hidden
                >
                  {opt.badge}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
